import { Spacing } from '@persona-id/design-tokens';
import { groupBy } from 'lodash/fp';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import Divider from 'dashboard/components/Divider';
import ListMatchEntity from 'dashboard/components/ReportResult/ListMatchEntity';
import { PersonaWatchlistMatchedEntities } from 'dashboard/components/ReportResult/Watchlist';
import TableLayout, {
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { ReportWatchlist, WatchlistIgnoreListEntry } from 'dashboard/models/report/watchlist';
import Button from 'design-system/components/Button';
import Sticker from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';
import { headingMixins, paragraphMixins } from 'design-system/mixins/text';
import { DASHED_DATE_FORMAT, DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';

const Separator = styled('span')`
  padding: 0px ${Spacing[2]};
`;

const RunHistoryTableTH = styled(TableTH)`
  padding: ${Spacing[3]} ${Spacing[2]};
`;

const RunHistoryTableTD = styled(TableTD)`
  padding: ${Spacing[4]} ${Spacing[2]};
`;

const SubTitle = styled.div`
  ${headingMixins.sm};
  font-weight: 500;
`;

const StyledList = styled.div`
  padding: ${Spacing[4]};
  margin: ${Spacing[2]} 0px;
  border-width: 1px;
  border-style: solid;
  border-color: ${({ theme }) => theme.color.border.secondary};
  border-radius: ${BORDER_RADIUSES.rounded};
`;

const SubText = styled.div`
  ${paragraphMixins.md}
  color: ${({ theme }) => theme.color.text.tertiary};
  margin: ${Spacing[2]} 0px;
`;

const Footer = styled.div`
  border-top: 1px solid ${({ theme }) => theme.color.border.secondary};
  padding-top: ${Spacing[4]};
  margin-top: ${Spacing[4]};
  margin-bottom: -${Spacing[5]};
`;

interface WatchlistIgnoreHistoryDetailsProps {
  entries: WatchlistIgnoreListEntry[];
  onBackClick: () => void;
}

export const WatchlistIgnoreHistoryDetails = ({
  entries,
  onBackClick,
}: WatchlistIgnoreHistoryDetailsProps) => {
  if (!entries) {
    return null;
  }

  return (
    <>
      <SubTitle>Dismissal Reason</SubTitle>
      <Paragraph>{entries[0]['ignore-reason']}</Paragraph>
      <SubText>
        {DateTime.fromISO(entries[0]['ignored-at']).toFormat(DATE_AND_TIME_FORMAT)}
        <Separator>•</Separator>
        {entries[0]['user']}
      </SubText>
      <Divider />
      <SubTitle>
        Dismissed Matches <Sticker>{entries.length}</Sticker>
      </SubTitle>
      <StyledList>
        {entries
          .map((entry) => entry.match)
          .filter((match) => !!match)
          .map((match, index) => {
            return (
              <ListMatchEntity
                key={`${match}-${index}`}
                url={match.url}
                name={match.name}
                flagCode={
                  match.slug === 'internal-data-source'
                    ? undefined
                    : match['flag-code'] || match['country-code']
                }
              >
                {match.entities && <PersonaWatchlistMatchedEntities entities={match.entities} />}
              </ListMatchEntity>
            );
          })}
      </StyledList>
      <Footer>
        <Button
          id='report-watchlist-ignore-history-detail__back_button'
          onClick={onBackClick}
          width='auto'
          variant='basic'
        >
          Back
        </Button>
      </Footer>
    </>
  );
};

interface WatchlistIgnoreHistoryTableProps {
  groupedMatches: { [ignoredAt: string]: WatchlistIgnoreListEntry[] };
  onRowClick: (ignoredAt: string) => void;
}

const WatchlistIgnoreHistoryTable = ({
  groupedMatches,
  onRowClick,
}: WatchlistIgnoreHistoryTableProps) => {
  return (
    <>
      <TableLayout
        isLoading={false}
        showHeader={true}
        isEmpty={Object.keys(groupedMatches).length === 0}
        nullState={
          <TableNullState dataTestBlock='report-watchlist-ignore-history-table__null-state'>
            No previously dismissed matches for this report.
          </TableNullState>
        }
        containerMaxHeight={500}
        headerTR={
          <>
            <RunHistoryTableTH>Dismissed At</RunHistoryTableTH>
            <RunHistoryTableTH>Matches</RunHistoryTableTH>
            <RunHistoryTableTH>Dismissed By</RunHistoryTableTH>
          </>
        }
      >
        {Object.entries(groupedMatches).map(([ignoredAt, entries]) => (
          <TableTR
            id={`report-ignore-history-table-row__${ignoredAt}`}
            key={ignoredAt}
            onClick={() => {
              onRowClick(ignoredAt);
            }}
          >
            <RunHistoryTableTD>
              {DateTime.fromISO(ignoredAt).toFormat(DASHED_DATE_FORMAT)}
            </RunHistoryTableTD>
            <RunHistoryTableTD>{entries.length}</RunHistoryTableTD>
            <RunHistoryTableTD>{entries[0]['user']}</RunHistoryTableTD>
          </TableTR>
        ))}
      </TableLayout>
    </>
  );
};

interface WatchlistIgnoreHistoryProps {
  report: ReportWatchlist;
}

const WatchlistIgnoreHistory = ({
  report: {
    attributes: { 'ignore-list': ignoreList },
  },
}: WatchlistIgnoreHistoryProps) => {
  const groupedMatches = groupBy('ignored-at', ignoreList);

  const [selectedRowEntries, setSelectedRowEntries] = useState<WatchlistIgnoreListEntry[] | null>(
    null,
  );

  const handleRowClick = (ignoredAt: string) => {
    setSelectedRowEntries(groupedMatches[ignoredAt]);
  };

  const handleBackClick = () => {
    setSelectedRowEntries(null);
  };

  if (selectedRowEntries) {
    return (
      <WatchlistIgnoreHistoryDetails entries={selectedRowEntries} onBackClick={handleBackClick} />
    );
  } else {
    return (
      <WatchlistIgnoreHistoryTable groupedMatches={groupedMatches} onRowClick={handleRowClick} />
    );
  }
};

export default WatchlistIgnoreHistory;
